import { useEffect, useMemo, useRef, useState } from 'react';

import { buildCalendarDateMeta, buildDateMeta } from '../../utils/dateUtils';
import { normalizeSelectionBrands } from '../../utils/brands';
import { filterSelectionRowsByReferenceId, getReferenceIdFilterList } from '../../utils/referenceIdFilters';
import { PROGRAM_CODE_MAP } from '../../utils/dataNormalizer';
import {
  isEnrollmentTerminated,
  resolveApiPresenceStatus,
  resolveEnrollmentLifecycleStatus,
} from '../../utils/enrollmentEvidence';

import { computeStatusMatchState, pickSelectionValue } from './utils';

export const useSelectionsModel = ({
  filteredData,
  referenceIdParam,
  fetchDiOrderItemsByReferenceId,
  setTableSnackbar,
}) => {
  const selectionRows = useMemo(() => {
    const rows = [];
    (filteredData || []).forEach((row) => {
      const selections = Array.isArray(row?.productSelections) ? row.productSelections : [];
      if (!selections.length) return;

      const terminationMeta = buildDateMeta(
        row?.terminationDate ?? row?.isTerminatedDate ?? row?.termDate ?? row?.terminationDt
      );
      const enrollmentMeta = buildDateMeta(row?.enrollmentDate ?? row?.enrolledDt ?? row?.enrollmentDt);
      const optOutMeta = buildDateMeta(row?.optOutDt ?? row?.optOutDate);
      const baseProgramCode = row?.programCode || row?.program || PROGRAM_CODE_MAP[row?.programName] || '';

      selections.forEach((selection, index) => {
        if (!selection) return;
        const selectionBrands = normalizeSelectionBrands(
          selection?.brands ?? selection?.brandCodes ?? selection?.brandNames ?? selection?.productBrands
        );

        const vendorProvidedMeta = buildCalendarDateMeta(
          pickSelectionValue(selection, [
            'vendorProvidedDate',
            'vendorProvidedDt',
            'vendorProvidedAt',
            'vendorProvidedTimestamp',
          ])
        );
        const selectionMeta = buildCalendarDateMeta(
          pickSelectionValue(selection, ['productSelectionDate', 'selectionDate', 'selectedAt', 'selectionDt'])
        );
        const goLiveMeta = buildCalendarDateMeta(
          pickSelectionValue(selection, ['goLiveDate', 'goLiveDt', 'goLiveAt', 'liveDate', 'liveDt'])
        );
        const cancelMeta = buildCalendarDateMeta(
          pickSelectionValue(selection, ['cancelDate', 'cancelDt', 'cancelAt', 'cancelledAt'])
        );
        const billingStartMeta = buildCalendarDateMeta(
          pickSelectionValue(selection, ['billingStartDate', 'billingStart', 'billingStartDt', 'billingStartAt'])
        );
        const billingEndMeta = buildCalendarDateMeta(
          pickSelectionValue(selection, ['billingEndDate', 'billingEnd', 'billingEndDt', 'billingEndAt'])
        );
        const effectiveMeta = buildCalendarDateMeta(
          pickSelectionValue(selection, ['effectiveDate', 'effectiveDt', 'effectiveAt', 'effectiveOn'])
        );

        const programCode = selection?.programCode || baseProgramCode;
        const id = `${row?.bac || row?.dealerCd || 'row'}-${programCode || 'program'}-${selection?.referenceId || index}`;

        rows.push({
          ...selection,
          id,
          bac: row?.bac ?? selection?.bac ?? '',
          dealerName: row?.dealerName ?? selection?.dealerName ?? '',
          dealerCd: row?.dealerCd ?? '',
          programCode,
          programName: row?.programName || selection?.programName || row?.programLabel || '',
          enrollmentStatusCd: resolveEnrollmentLifecycleStatus(row) || 'LIFECYCLE_UNAVAILABLE',
          apiPresenceStatus: resolveApiPresenceStatus(row),
          isTerminated: isEnrollmentTerminated(row),
          syntheticCancellation: row?.syntheticCancellation,
          syntheticCancellationState: row?.syntheticCancellationState,
          vendorInvoiceNumber:
            selection?.vendorInvoiceNumber ||
            selection?.vendorInvoiceNbr ||
            selection?.invoiceNumber ||
            row?.bac ||
            '',
          productBrandCode:
            selection?.productBrandCode ||
            selection?.productBrandCd ||
            selection?.productCd ||
            '',
          terminationDateDisplay: terminationMeta.display,
          terminationDateSort: terminationMeta.sort,
          enrollmentDateDisplay: enrollmentMeta.display,
          enrollmentDateSort: enrollmentMeta.sort,
          optOutDateDisplay: optOutMeta.display,
          optOutDateSort: optOutMeta.sort,
          brandsDisplay: selectionBrands.display,
          brandsCodes: selectionBrands.codes,
          vendorProvidedDate: vendorProvidedMeta.display || '',
          vendorProvidedDateDisplay: vendorProvidedMeta.display,
          vendorProvidedDateSort: vendorProvidedMeta.sort,
          productSelectionDate: selectionMeta.display || '',
          productSelectionDateDisplay: selectionMeta.display,
          productSelectionDateSort: selectionMeta.sort,
          goLiveDate: goLiveMeta.display || '',
          goLiveDateDisplay: goLiveMeta.display,
          goLiveDateSort: goLiveMeta.sort,
          cancelDate: cancelMeta.display || '',
          cancelDateDisplay: cancelMeta.display,
          cancelDateSort: cancelMeta.sort,
          billingStartDate: billingStartMeta.display || '',
          billingStartDateDisplay: billingStartMeta.display,
          billingStartDateSort: billingStartMeta.sort,
          billingEndDate: billingEndMeta.display || '',
          billingEndDateDisplay: billingEndMeta.display,
          billingEndDateSort: billingEndMeta.sort,
          effectiveDate: effectiveMeta.display || '',
          effectiveDateDisplay: effectiveMeta.display,
          effectiveDateSort: effectiveMeta.sort,
          sourceEnrollment: row,
        });
      });
    });

    return rows;
  }, [filteredData]);

  const referenceIdFilterList = useMemo(() => {
    return getReferenceIdFilterList(referenceIdParam);
  }, [referenceIdParam]);

  const referenceFilteredSelectionRows = useMemo(() => {
    return filterSelectionRowsByReferenceId(selectionRows, referenceIdFilterList);
  }, [referenceIdFilterList, selectionRows]);

  const [diByReferenceId, setDiByReferenceId] = useState({});
  const diLoadedRefsRef = useRef(new Set());
  const diErrorShownRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      const refs = Array.from(
        new Set(
          (referenceFilteredSelectionRows || [])
            .map((row) => (row?.referenceId != null ? String(row.referenceId).trim() : ''))
            .filter((v) => v.length > 0)
        )
      );
      if (!refs.length) return;
      const missing = refs.filter((ref) => !diLoadedRefsRef.current.has(ref));
      if (!missing.length) return;
      try {
        const result = await fetchDiOrderItemsByReferenceId(missing);
        const byRef = result?.byReferenceId || {};
        missing.forEach((ref) => diLoadedRefsRef.current.add(ref));
        if (!mounted) return;
        setDiByReferenceId((prev) => ({ ...(prev || {}), ...(byRef || {}) }));
      } catch (error) {
        if (!mounted) return;
        if (!diErrorShownRef.current) {
          diErrorShownRef.current = true;
          setTableSnackbar?.({
            open: true,
            severity: 'warning',
            message:
              error?.response?.data?.message ||
              error?.message ||
              'Failed to load DI fields (AppSheet cache unavailable).',
          });
        }
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [referenceFilteredSelectionRows, fetchDiOrderItemsByReferenceId, setTableSnackbar]);

  const diEnrichedSelectionRows = useMemo(() => {
    const map = diByReferenceId || {};
    return (referenceFilteredSelectionRows || []).map((row) => {
      const ref = row?.referenceId != null ? String(row.referenceId).trim() : '';
      if (!ref) return row;
      const di = map?.[ref];
      if (!di) return row;
      return { ...row, ...di };
    });
  }, [referenceFilteredSelectionRows, diByReferenceId]);

  const [statusMatchFilter, setStatusMatchFilter] = useState(null);

  const statusMatchChartData = useMemo(() => {
    const states = (diEnrichedSelectionRows || [])
      .map((row) => computeStatusMatchState(row))
      .filter((state) => state !== null);
    const matchCount = states.filter((state) => state === 'match').length;
    const mismatchCount = states.length - matchCount;
    return [
      { id: 0, value: matchCount, label: 'Match', color: 'rgba(75, 192, 192, 0.6)' },
      { id: 1, value: mismatchCount, label: 'Mismatch', color: 'rgba(255, 99, 132, 0.6)' },
    ];
  }, [diEnrichedSelectionRows]);

  const filteredSelectionRows = useMemo(() => {
    if (!statusMatchFilter) return diEnrichedSelectionRows;
    const desiredState = statusMatchFilter === 'Match' ? 'match' : 'mismatch';
    return diEnrichedSelectionRows.filter((row) => computeStatusMatchState(row) === desiredState);
  }, [diEnrichedSelectionRows, statusMatchFilter]);

  return {
    referenceFilteredSelectionRows,
    filteredSelectionRows,
    statusMatchChartData,
    statusMatchFilter,
    setStatusMatchFilter,
  };
};
